import React, { useState, useEffect } from "react";

// ── Onboarding steps ──────────────────────────────────────────────────────────

const STEPS = [
  {
    id: "welcome",
    icon: "🎉",
    title: "Bem-vindo ao Safe Zap!",
    description: "Sua central de atendimento WhatsApp com IA. Vamos dar os primeiros passos para você começar a atender com agilidade.",
    tip: null,
    action: null,
    page: null,
  },
  {
    id: "whatsapp",
    icon: "📱",
    title: "Conectar o WhatsApp",
    description: "Para receber e enviar mensagens, conecte seu número via QR Code. É rápido e funciona com qualquer plano do WhatsApp.",
    tip: "Acesse Configurações → WhatsApp → Conectar. Escaneie o QR Code com o aplicativo no celular.",
    action: "Ir para Configurações",
    page: "config",
  },
  {
    id: "company",
    icon: "🏢",
    title: "Configurar sua empresa",
    description: "Preencha o nome, logo (em breve) e ative a assinatura automática para que os clientes identifiquem quem está atendendo.",
    tip: "Em Configurações → Geral você define o nome da empresa e ativa a assinatura nas mensagens.",
    action: "Abrir Configurações",
    page: "config",
  },
  {
    id: "team",
    icon: "👥",
    title: "Convidar sua equipe",
    description: "Adicione atendentes e supervisores. Cada membro recebe seu próprio login e as conversas podem ser distribuídas automaticamente.",
    tip: "Em Configurações → Usuários crie os membros da equipe com e-mail e senha.",
    action: "Gerenciar usuários",
    page: "config",
  },
  {
    id: "contacts",
    icon: "📋",
    title: "Importar contatos",
    description: "Seus contatos são criados automaticamente quando recebem mensagens. Mas você também pode cadastrar manualmente ou importar.",
    tip: "Em Contatos clique em '+ Novo contato' ou use a importação em massa (CSV em breve).",
    action: "Ver Contatos",
    page: "contatos",
  },
  {
    id: "chatbot",
    icon: "🤖",
    title: "Criar seu primeiro Chatbot",
    description: "Configure um fluxo automático para receber clientes fora do horário comercial, coletar dados ou direcionar para o atendente certo.",
    tip: "Em Chatbot clique em '+ Novo chatbot', configure palavras-chave de disparo e monte o fluxo com perguntas e respostas.",
    action: "Configurar Chatbot",
    page: "chatbot",
  },
  {
    id: "ai",
    icon: "✨",
    title: "Usar a Safe IA",
    description: "Durante um atendimento, clique no botão 'Safe IA' para receber sugestões inteligentes de resposta baseadas no histórico da conversa.",
    tip: "A IA analisa o sentimento e urgência do cliente e sugere 3 respostas contextualizadas. Clique para usar e edite se necessário. Configure ANTHROPIC_API_KEY para IA mais avançada.",
    action: "Ver Conversas",
    page: "atendimentos",
  },
  {
    id: "automations",
    icon: "⚡",
    title: "Automatizar tarefas",
    description: "Crie regras que executam ações automaticamente: responder palavras-chave, atribuir conversas, criar leads, mudar status.",
    tip: "Em Automações defina o gatilho (ex: nova conversa) e a ação (ex: enviar mensagem de boas-vindas).",
    action: "Criar Automação",
    page: "automacoes",
  },
  {
    id: "done",
    icon: "🚀",
    title: "Tudo pronto!",
    description: "Você está pronto para atender clientes com eficiência. Explore o painel, os relatórios e o módulo de tickets para gestão completa.",
    tip: "Dica: use o painel de Supervisor para monitorar todos os atendimentos em tempo real.",
    action: "Ir ao Painel",
    page: "dashboard",
  },
];

const STORAGE_KEY = "sz_onboarding_v1";

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { step: 0, completed: false, dismissed: false, triggerHidden: false };
    return { triggerHidden: false, ...JSON.parse(raw) };
  } catch {
    return { step: 0, completed: false, dismissed: false, triggerHidden: false };
  }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

// ── Progress dots ─────────────────────────────────────────────────────────────

function StepDots({ total, current }) {
  return (
    <div className="onboardingDots">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`onboardingDot ${i === current ? "active" : i < current ? "done" : ""}`}
        />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// Custom event to open onboarding from anywhere
export const OPEN_ONBOARDING_EVENT = "sz-open-onboarding";

export function openOnboarding() {
  window.dispatchEvent(new CustomEvent(OPEN_ONBOARDING_EVENT));
}

// ── Inline trigger button (e.g. below dashboard cards) ────────────────────────
export function OnboardingTrigger({ className = "", inline = false }) {
  const [snapshot, setSnapshot] = useState(() => loadState());
  useEffect(() => {
    const sync = () => setSnapshot(loadState());
    window.addEventListener("storage", sync);
    window.addEventListener(OPEN_ONBOARDING_EVENT + "-state", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(OPEN_ONBOARDING_EVENT + "-state", sync);
    };
  }, []);

  // User chose to hide the trigger permanently — render nothing
  if (snapshot.triggerHidden) return null;

  function hideForever(e) {
    e.stopPropagation();
    e.preventDefault();
    const next = { ...loadState(), triggerHidden: true };
    saveState(next);
    setSnapshot(next);
    window.dispatchEvent(new Event(OPEN_ONBOARDING_EVENT + "-state"));
  }

  const completed = snapshot.completed;
  const label = completed ? "Guia concluído" : "Início rápido";
  const icon  = completed ? "✓" : "?";

  return (
    <span
      className={`onboardingTriggerWrap ${inline ? "inline" : ""} ${className}`}
      role="presentation"
    >
      <button
        className={`onboardingTrigger compact ${completed ? "done" : ""}`}
        onClick={openOnboarding}
        aria-label={label}
        title={label}
        type="button"
      >
        <span className="onboardingTriggerIcon">{icon}</span>
        <span className="onboardingTriggerLabel">{label}</span>
      </button>
      <button
        className="onboardingTriggerHide"
        onClick={hideForever}
        aria-label="Ocultar permanentemente"
        title="Não mostrar mais"
        type="button"
      >
        ×
      </button>
    </span>
  );
}

function OnboardingFlow({ onNavigate, hideTrigger = false }) {
  const [state, setState] = useState(() => loadState());
  const [open, setOpen] = useState(false);

  // Show automatically on first ever visit
  useEffect(() => {
    const saved = loadState();
    if (!saved.completed && !saved.dismissed) {
      setOpen(true);
    }
  }, []);

  // Listen for global open-onboarding events (from inline triggers)
  useEffect(() => {
    const handler = () => {
      const saved = loadState();
      if (saved.completed) {
        const fresh = { step: 0, completed: false, dismissed: false };
        saveState(fresh);
        setState(fresh);
        window.dispatchEvent(new Event(OPEN_ONBOARDING_EVENT + "-state"));
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_ONBOARDING_EVENT, handler);
    return () => window.removeEventListener(OPEN_ONBOARDING_EVENT, handler);
  }, []);

  function updateState(patch) {
    setState((prev) => {
      const next = { ...prev, ...patch };
      saveState(next);
      return next;
    });
  }

  function goNext() {
    const nextStep = state.step + 1;
    if (nextStep >= STEPS.length) {
      updateState({ completed: true });
      setOpen(false);
    } else {
      updateState({ step: nextStep });
    }
  }

  function goPrev() {
    if (state.step > 0) updateState({ step: state.step - 1 });
  }

  function dismiss() {
    updateState({ dismissed: true });
    setOpen(false);
  }

  function restart() {
    const fresh = { step: 0, completed: false, dismissed: false };
    saveState(fresh);
    setState(fresh);
    setOpen(true);
  }

  function handleAction(page) {
    if (page && onNavigate) onNavigate(page);
    goNext();
  }

  const step = STEPS[Math.min(state.step, STEPS.length - 1)];
  const isLast = state.step === STEPS.length - 1;
  const isFirst = state.step === 0;
  const progress = Math.round((state.step / (STEPS.length - 1)) * 100);

  // Update progress signal so trigger refreshes
  useEffect(() => {
    window.dispatchEvent(new Event(OPEN_ONBOARDING_EVENT + "-state"));
  }, [state.completed]);

  return (
    <>
      {/* Floating trigger — only when not hidden (default) and not on dashboard */}
      {!open && !hideTrigger && !state.triggerHidden && (
        <span className="onboardingTriggerWrap floating" role="presentation">
          <button
            className={`onboardingTrigger compact ${state.completed ? "done" : ""}`}
            onClick={() => (state.completed ? restart() : setOpen(true))}
            aria-label={state.completed ? "Reiniciar tour" : "Abrir guia de início rápido"}
            title={state.completed ? "Reiniciar tour" : "Abrir guia de início rápido"}
            type="button"
          >
            <span className="onboardingTriggerIcon">{state.completed ? "✓" : "?"}</span>
            <span className="onboardingTriggerLabel">{state.completed ? "Guia concluído" : "Início rápido"}</span>
          </button>
          <button
            className="onboardingTriggerHide"
            onClick={(e) => {
              e.stopPropagation();
              updateState({ triggerHidden: true });
            }}
            aria-label="Ocultar permanentemente"
            title="Não mostrar mais"
            type="button"
          >
            ×
          </button>
        </span>
      )}

      {/* Overlay */}
      {open && (
        <div className="onboardingOverlay">
          <div className="onboardingModal">
            {/* Header */}
            <div className="onboardingModalHeader">
              <div className="onboardingBrand">
                <span className="onboardingBrandLogo">⚡</span>
                <span>Guia de início rápido</span>
              </div>
              <button className="onboardingCloseBtn" onClick={dismiss} title="Fechar e não mostrar novamente">✕</button>
            </div>

            {/* Progress bar */}
            <div className="onboardingProgressBar">
              <div className="onboardingProgressFill" style={{ width: `${progress}%` }} />
            </div>

            {/* Content */}
            <div className="onboardingContent">
              <div className="onboardingStepIcon">{step.icon}</div>
              <div className="onboardingStepCounter">Passo {state.step + 1} de {STEPS.length}</div>
              <h3 className="onboardingStepTitle">{step.title}</h3>
              <p className="onboardingStepDesc">{step.description}</p>

              {step.tip && (
                <div className="onboardingTip">
                  <span className="onboardingTipIcon">💡</span>
                  <span>{step.tip}</span>
                </div>
              )}
            </div>

            {/* Step dots */}
            <StepDots total={STEPS.length} current={state.step} />

            {/* Actions */}
            <div className="onboardingActions">
              {!isFirst && (
                <button className="onboardingSecBtn" onClick={goPrev}>← Anterior</button>
              )}
              {isFirst && (
                <button className="onboardingSecBtn" onClick={dismiss}>Pular tour</button>
              )}
              <div className="onboardingActionsRight">
                {step.action && step.page && (
                  <button className="onboardingNavBtn" onClick={() => handleAction(step.page)}>
                    {step.action} →
                  </button>
                )}
                <button className="onboardingNextBtn" onClick={goNext}>
                  {isLast ? "Concluir 🎉" : "Próximo →"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OnboardingFlow;
